
import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedCard } from '@/components/ui/AnimatedCard';
import { gasFlowData } from '@/data/gas-analytics-data';
import { getTooltipStyle } from '@/components/telemetry/utils/chartUtils';

const GasFlowChart = () => {
  const [hideLines, setHideLines] = useState<Record<string, boolean>>({
    flow: false
  });

  // Toggle line visibility when legend is clicked
  const handleLegendClick = (dataKey: string) => {
    setHideLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey]
    }));
  };

  // Custom legend renderer
  const renderLegend = () => {
    const payload = [
      { dataKey: 'flow', value: 'Flow Rate (m³/h)', color: '#FBBF24' }
    ];
    
    return (
      <ul className="flex flex-wrap justify-center gap-3 mt-4">
        {payload.map((entry, index) => (
          <li 
            key={`item-${index}`}
            className={`flex items-center gap-2 cursor-pointer px-3 py-1.5 rounded-md transition-colors ${
              hideLines[entry.dataKey] 
                ? "opacity-60 bg-gray-100 dark:bg-gray-700" 
                : "bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
            }`}
            onClick={() => handleLegendClick(entry.dataKey)}
          >
            <span 
              className="inline-block w-3 h-3 rounded-full" 
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm font-medium">{entry.value}</span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <AnimatedCard 
      animation="fade-up"
      delay={100}
      className="bg-white dark:bg-gray-800 border-none"
    >
      <CardHeader>
        <CardTitle>Gas Flow Monitoring (m³/h)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={gasFlowData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip 
                formatter={(value) => [`${value} m³/h`, 'Flow Rate']}
                contentStyle={getTooltipStyle()}
              />
              <Line 
                type="monotone" 
                dataKey="flow" 
                name="Flow Rate"
                stroke="#FBBF24" 
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                hide={hideLines.flow}
              />
            </LineChart>
          </ResponsiveContainer>
          {/* Render custom legend */}
          {renderLegend()}
        </div>
      </CardContent>
    </AnimatedCard>
  );
};

export default GasFlowChart;
